import axios from 'axios';

const API = '/api';

axios.defaults.timeout = 30000;

const retryRequest = async (fn, retries = 2, delay = 1000) => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0 && (error.code === 'ECONNABORTED' || error.response?.status >= 500)) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryRequest(fn, retries - 1, delay * 1.5);
    }
    throw error;
  }
};

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const fetchReports = async () => {
  const res = await axios.get(`${API}/reports`);
  return res.data;
};

export const fetchNotifications = async () => {
  const res = await axios.get(`${API}/notifications`);
  return res.data;
};

export const fetchStats = async () => {
  const res = await axios.get(`${API}/admin/stats`);
  return res.data;
};

export const fetchConsultantStats = async () => {
  const res = await axios.get(`${API}/consultant/stats`);
  return res.data;
};

export const fetchReport = async (reportId) => {
  const res = await axios.get(`${API}/reports/${reportId}`);
  return res.data;
};

export const createReport = async (reportData) => {
  const res = await axios.post(`${API}/reports`, reportData);
  return res.data;
};

export const updateReport = async (reportId, reportData) => {
  const res = await axios.put(`${API}/reports/${reportId}`, reportData);
  return res.data;
};

export const deleteReport = async (reportId) => {
  const res = await axios.delete(`${API}/reports/${reportId}`);
  return res.data;
};

export const markNotificationRead = async (notificationId) => {
  const res = await axios.put(`${API}/notifications/${notificationId}/read`);
  return res.data;
};

export const markAllNotificationsRead = async () => {
  const res = await axios.put(`${API}/notifications/read-all`);
  return res.data;
};

export const fetchAllUsers = async () => {
  const res = await axios.get(`${API}/admin/users`);
  return res.data;
};

export const updateUser = async (userId, userData) => {
  const res = await axios.put(`${API}/admin/users/${userId}`, userData);
  return res.data;
};

export const deleteUser = async (userId) => {
  const res = await axios.delete(`${API}/admin/users/${userId}`);
  return res.data;
};

export const generatePublicLink = async (reportId) => {
  const res = await axios.post(`${API}/reports/${reportId}/generate-link`);
  return res.data;
};

export const revokePublicLink = async (reportId) => {
  const res = await axios.delete(`${API}/reports/${reportId}/public-link`);
  return res.data;
};

export const fetchPublicReport = async (publicToken) => {
  const res = await axios.get(`${API}/public/report/${publicToken}`);
  return res.data;
};

export const fetchConsultants = async () => {
  const res = await axios.get(`${API}/consultants`);
  return res.data;
};

export const createConsultant = async (consultantData) => {
  const res = await axios.post(`${API}/consultants`, consultantData);
  return res.data;
};

export const updateConsultant = async (consultantId, consultantData) => {
  const res = await axios.put(`${API}/consultants/${consultantId}`, consultantData);
  return res.data;
};

export const deleteConsultant = async (consultantId) => {
  const res = await axios.delete(`${API}/consultants/${consultantId}`);
  return res.data;
};

export const uploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await axios.post(`${API}/upload-image`, formData);
  return res.data;
};

export const registerUser = async (userData) => {
  const res = await axios.post(`${API}/auth/register`, userData);
  return res.data;
};

const api = {
  get: (url, config) => axios.get(`${API}${url}`, config),
  post: (url, data, config) => axios.post(`${API}${url}`, data, config),
  put: (url, data) => axios.put(`${API}${url}`, data),
  delete: (url) => axios.delete(`${API}${url}`)
};

export default api;
